import React, { useContext, useEffect, useState } from 'react'
import i18n from 'live/services/i18n'
import ConfirmationDialog from 'live/components/actions/ui/confirmation-dialog'
import { editProductTreeElement, postAsset } from '../common/apis'
import { handleResponse, updateAssetTreeState } from '../common/utils'
import { configsContext } from './AssetConfigs'
import { DialogContent } from './dialogContent'
import { capitalize, cloneDeep } from 'lodash'
import { getNamesFromTableArray, getSkusPostJson, handleImageEdit, postImage } from './utils'
interface IProps {
    showModal: boolean
    setShowModal: (show: boolean) => void
    currentName?: string
    parentProduct?: string
    currentId?: string
    isEdit: boolean
    currentSkuColor?: string
    currentProductImageId?: string
}
const defaultData = [
    ['', ''],
    ['', ''],
    ['', '']
]

export function ProductDialog({
    showModal,
    setShowModal,
    currentId,
    currentName,
    parentProduct,
    isEdit,
    currentSkuColor,
    currentProductImageId
}: IProps): JSX.Element {
    const contentType = parentProduct ? 'sku' : 'product'
    const [tableDataArray, setTableDataArray] = useState<string[][]>(cloneDeep(defaultData))
    const [assetName, setAssetName] = useState(isEdit ? currentName : '')
    const [assetId, setAssetId] = useState(isEdit ? currentId : '')
    const [skuColor, setSkuColor] = useState(isEdit ? currentSkuColor : 'rgba(255, 255, 255, 1)')
    const [showErrors, setShowErrors] = useState(false)
    const [errorInInputs, setErrorInInputs] = useState(true)
    const [imageFile, setImageFile] = useState<FileType | undefined>()

    const { setAssetTree } = useContext(configsContext)

    function handleHide(): void {
        setShowModal(false)
        setTableDataArray(cloneDeep(defaultData))
        setAssetName('')
        setAssetId('')
    }

    useEffect(() => {
        const isError = checkIfInputsHaveErrors()

        setErrorInInputs(isError)
        if (!isError && showErrors) setShowErrors(false)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assetId, assetName, skuColor, currentId, currentName, imageFile])

    function checkIfInputsHaveErrors(): boolean {
        const assetDoesntHaveName = !assetName
        const nameDidntChange = assetName === currentName
        const idDidntChange = assetId === currentId
        const imageIdDidntChange = !imageFile
        const colorDidntChange = skuColor === currentSkuColor

        if (assetDoesntHaveName) return true

        return isEdit && nameDidntChange && idDidntChange && imageIdDidntChange && colorDidntChange
    }

    function postProduct(imageId?: string): Promise<void | Response> {
        const params = { productName: assetName?.trim(), productId: assetId?.trim(), productImageId: imageId }

        return postAsset('product', params).then(postResponse => {
            handleResponse(postResponse, 'product')
            updateAssetTreeState(postResponse.status, 'product', setAssetTree)

            return postResponse
        })
    }

    function postSku(): void {
        const params = { skuName: assetName?.trim(), skuId: assetId?.trim(), productName: parentProduct, skuColor }

        postAsset('sku', params).then(postResponse => {
            handleResponse(postResponse, 'sku')
            updateAssetTreeState(postResponse.status, 'product', setAssetTree)
        })
    }

    function postSkus(): void {
        const params = getSkusPostJson(tableDataArray, assetName?.trim())

        postAsset('skus', params).then(postResponse => {
            handleResponse(postResponse, 'product')
            updateAssetTreeState(postResponse.status, 'product', setAssetTree)
        })
    }
    function editProduct(imageId: string): void {
        const editIdParams: EditProductTreeParams = {
            productId: assetId,
            productName: currentName,
            productImageId: imageId,
            contentType: 'product'
        }

        if (assetName !== currentName) editIdParams.newName = assetName

        editProductTreeElement(editIdParams).then(idPutResponse => {
            handleResponse(idPutResponse, 'product')
            updateAssetTreeState(idPutResponse.status, 'product', setAssetTree)
        })
    }

    function editSku(): void {
        const editIdParams: EditProductTreeParams = {
            skuId: assetId,
            skuName: currentName,
            skuColor: skuColor,
            contentType: 'sku',
            productName: parentProduct
        }

        if (assetName !== currentName) editIdParams.newName = assetName

        editProductTreeElement(editIdParams).then(idPutResponse => {
            handleResponse(idPutResponse, 'sku')
            updateAssetTreeState(idPutResponse.status, 'product', setAssetTree)
        })
    }

    function handleApplySku(): void {
        if (errorInInputs) {
            setShowErrors(true)
        } else if (isEdit) {
            editSku()
        } else {
            postSku()
        }
    }

    async function handleApplyProduct(): Promise<void> {
        let imageId = ''

        if (errorInInputs) {
            setShowErrors(true)
        } else if (isEdit) {
            if (imageFile) await handleImageEdit(imageFile, currentProductImageId, assetName).then(id => (imageId = id))
            editProduct(imageId)
        } else {
            if (imageFile) await postImage(imageFile).then(id => (imageId = id))
            postProduct(imageId)
                .then(response => {
                    if (response.ok) {
                        if (getNamesFromTableArray(tableDataArray).length) postSkus()
                    }
                })
                .catch(error => {
                    console.log(error)
                })
        }
    }

    return (
        <ConfirmationDialog
            show={showModal}
            onHide={handleHide}
            title={` ${capitalize(i18n(isEdit ? 'edit' : 'add new'))} ${capitalize(i18n(contentType))}`}
            btnStyle={errorInInputs ? 'notAllowed' : 'primary'}
            cancelText={i18n('cancel')}
            className={'addNewFactory-confirmation-modal modal-sm'}
            content={
                <DialogContent
                    contentType={contentType}
                    assetName={assetName}
                    setAssetName={setAssetName}
                    secondaryValue={assetId}
                    setSecondaryValue={setAssetId}
                    showErrors={showErrors}
                    tableDataArray={tableDataArray}
                    setTableDataArray={setTableDataArray}
                    setSkuColor={setSkuColor}
                    skuColor={skuColor}
                    isEdit={isEdit}
                    currentProductImageId={currentProductImageId}
                    setImageFile={setImageFile}
                />
            }
            btnText={i18n(isEdit ? 'Update' : 'Save')}
            apply={parentProduct ? handleApplySku : handleApplyProduct}
        />
    )
}
